import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from '@myua/kit';
import { getAssetLabLogger } from '../app/logs';
import * as magicStudioActions from './actions';
import { MagicPrimaryAsset, MAX_REFS, uploadRef } from './model';
import { getBasket, getBasketMap } from './selectors';

// Where a basket interaction originated, for product logging.
export type BasketSource = 'sidebar' | 'fullscreen' | 'tray';

// Shared basket controller used by every entry point (detail panel, fullscreen,
// the floating tray). Centralises the MAX_REFS cap + its toast so the rule lives
// in one place rather than in each button.
export function useBasket() {
  const dispatch = useDispatch();
  const basket = useSelector(getBasket);
  const map = useSelector(getBasketMap);
  const assetLabLogger = useSelector(getAssetLabLogger);

  const full = basket.length >= MAX_REFS;

  const isInBasket = useCallback((id: number) => !!map[id], [map]);

  // `source` tells the product log where the click came from. Defaults to the
  // sidebar (the primary per-asset entry point).
  const toggle = useCallback(
    (asset: MagicPrimaryAsset, source: BasketSource = 'sidebar') => {
      if (!map[asset.id] && basket.length >= MAX_REFS) {
        void message.warning(`Up to ${MAX_REFS} references in the basket`);
        return;
      }
      // Only an add (not a removal) is a "stage into Magic Studio" event.
      if (!map[asset.id]) {
        assetLabLogger({
          action: 'Magic Studio',
          sub_action: 'Add',
          extra: { int_1: asset.id, text_1: source === 'tray' ? 'sidebar' : source },
        });
      }
      dispatch(magicStudioActions.basketToggled({ asset }));
    },
    [dispatch, map, basket.length, assetLabLogger],
  );

  // Stage a frame captured from the fullscreen viewer (video / 3D). Always an
  // add (each capture is a distinct image), subject to the cap.
  const addCapture = useCallback(
    (name: string, dataUrl: string) => {
      if (basket.length >= MAX_REFS) {
        void message.warning(`Up to ${MAX_REFS} references in the basket`);
        return;
      }
      assetLabLogger({
        action: 'Magic Studio',
        sub_action: 'Add',
        extra: { int_1: null, text_1: 'capture' },
      });
      dispatch(magicStudioActions.basketCaptureAdded({ ref: uploadRef(name, dataUrl) }));
      void message.success('Frame added to Magic Studio');
    },
    [dispatch, basket.length, assetLabLogger],
  );

  const remove = useCallback(
    (key: string) => dispatch(magicStudioActions.basketRemoved({ key })),
    [dispatch],
  );

  // Clears the whole staged session — the references *and* the prompt /
  // settings / result stashed by the last close. "Clear" has to mean "nothing
  // is waiting for me" or the tray would keep offering a session to resume.
  const clear = useCallback(() => dispatch(magicStudioActions.sessionCleared()), [dispatch]);

  // Open the studio with the basket preloaded as references (or empty when the
  // basket is empty — the user pastes / uploads from scratch).
  const open = useCallback(
    (source: BasketSource = 'tray') => {
      assetLabLogger({
        action: 'Magic Studio',
        sub_action: 'Open',
        extra: { int_1: basket.length, text_1: source },
      });
      dispatch(magicStudioActions.opened({ refs: basket }));
    },
    [dispatch, basket, assetLabLogger],
  );

  return { basket, isInBasket, toggle, addCapture, remove, clear, open, full };
}
